import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Save, Home, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { HOMEPAGE_DEFAULTS, mergeContent } from "@/lib/homepageContent";

// Immutable get/set of a nested value by path array.
function getAt(obj, path) {
  return path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setAt(obj, path, val) {
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[head] = rest.length ? setAt(obj[head] ?? {}, rest, val) : val;
  return clone;
}

export default function AdminHomepage() {
  // Start from defaults so the form always renders even before load finishes.
  const [content, setContent] = useState(() => mergeContent(HOMEPAGE_DEFAULTS, {}));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/config");
      setContent(mergeContent(HOMEPAGE_DEFAULTS, data?.homepage));
    } catch {
      setContent(mergeContent(HOMEPAGE_DEFAULTS, {}));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (path, val) => setContent((prev) => setAt(prev, path, val));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/config", { homepage: content });
      toast.success("Homepagina opgeslagen — direct live voor bezoekers.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  const resetSection = (key) => {
    setContent((prev) => setAt(prev, [key], mergeContent(HOMEPAGE_DEFAULTS[key], {})));
    toast.message("Sectie teruggezet naar standaardtekst — vergeet niet op te slaan.");
  };

  // ---- small field helpers (close over `content` + `update`) ----
  const Field = ({ label, path, area = false }) => {
    const Comp = area ? Textarea : Input;
    return (
      <div className="space-y-1.5">
        <Label className="text-[13px] text-oc-textSecondary">{label}</Label>
        <Comp
          value={getAt(content, path) ?? ""}
          onChange={(e) => update(path, e.target.value)}
          className={area ? "min-h-[90px] rounded-oc-btn" : "h-11 rounded-oc-btn"}
        />
      </div>
    );
  };

  // Render a fixed-length list. With `fields`, each item is an object and gets
  // one field per entry. Without `fields`, each item is a plain string edited
  // directly at its index.
  const List = ({ basePath, itemLabel, fields }) => {
    const arr = getAt(content, basePath) || [];
    if (!fields) {
      return (
        <div className="space-y-3">
          {arr.map((_, i) => (
            <Field key={i} label={`${itemLabel} ${i + 1}`} path={[...basePath, i]} />
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {arr.map((_, i) => (
          <div key={i} className="rounded-oc border border-oc-border p-4 space-y-3 bg-oc-subtle/40">
            <p className="text-[12px] uppercase tracking-wide font-semibold text-oc-textSecondary">
              {itemLabel} {i + 1}
            </p>
            {fields.map((f) => (
              <Field key={f.k} label={f.label} area={f.area} path={[...basePath, i, f.k]} />
            ))}
          </div>
        ))}
      </div>
    );
  };

  const Section = ({ value, title, sub, children, sectionKey }) => (
    <AccordionItem value={value} className="border border-oc-border rounded-oc mb-3 px-5 bg-white">
      <AccordionTrigger className="font-display font-semibold text-oc-primary text-[17px] py-4 hover:no-underline">
        {title}
      </AccordionTrigger>
      <AccordionContent className="pb-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] text-oc-textSecondary">{sub}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => resetSection(sectionKey)}
            className="text-oc-textSecondary hover:text-oc-primary text-[12px]"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Standaardtekst
          </Button>
        </div>
        <div className="space-y-4">{children}</div>
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-oc-primary text-[34px] leading-tight">Homepagina</h1>
          <p className="text-oc-textSecondary text-[14px]">
            Bewerk alle teksten van de homepagina. Laat een veld leeg om de standaardtekst te tonen.
          </p>
        </div>
        <Button data-testid="save-homepage" onClick={save} disabled={busy} className="rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white px-5 shrink-0">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Opslaan..." : "Opslaan"}
        </Button>
      </div>

      <div className="flex items-start gap-2.5 bg-oc-subtle rounded-oc p-4 text-[13px] text-oc-textSecondary">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-oc-secondary" />
        <p>
          Wijzigingen zijn meteen zichtbaar voor bezoekers. In de <strong>broncode die Google ziet</strong>{" "}
          (view-source) verschijnen ze pas na de volgende prerender-run (de nachtelijke taak, of een
          handmatige prerender na deploy).
        </p>
      </div>

      <Accordion type="single" collapsible defaultValue="hero" className="w-full">
        <Section value="hero" sectionKey="hero" title="1 · Hero (bovenaan)" sub="De grote titel, subtitel, knop en vertrouwensbadges.">
          <Field label="Eyebrow / klein label" path={["hero", "eyebrow"]} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Titel — eerste deel" path={["hero", "headlineLead"]} />
            <Field label="Titel — gekleurd deel" path={["hero", "headlineAccent"]} />
          </div>
          <Field label="Subtitel" path={["hero", "subtitleLead"]} area />
          <Field label="Subtitel — vetgedrukt slot" path={["hero", "subtitleAccent"]} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Knoptekst (CTA)" path={["hero", "ctaLabel"]} />
            <Field label="Tekst onder de knop" path={["hero", "ctaSub"]} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Badge — score" path={["hero", "badgeRatingValue"]} />
            <Field label="Badge — score-omschrijving" path={["hero", "badgeRatingLabel"]} />
            <Field label="Badge — GDPR" path={["hero", "badgeGdpr"]} />
            <Field label="Badge — land" path={["hero", "badgeCountry"]} />
            <Field label="Badge — doelgroep" path={["hero", "badgeAudience"]} />
          </div>
        </Section>

        <Section value="stats" sectionKey="stats" title="2 · Statistieken-balk" sub="Vier tellers. Getal + voor-/achtervoegsel + omschrijving.">
          <List
            basePath={["stats"]}
            itemLabel="Statistiek"
            fields={[
              { k: "value", label: "Getal (bv. 4847 of 1.2)" },
              { k: "prefix", label: "Voorvoegsel (bv. €)" },
              { k: "suffix", label: "Achtervoegsel (bv. +, %, M+, sec)" },
              { k: "label", label: "Omschrijving" },
            ]}
          />
        </Section>

        <Section value="how" sectionKey="howItWorks" title="3 · Hoe werkt het" sub="Introtekst en de 3 stappen.">
          <Field label="Eyebrow" path={["howItWorks", "eyebrow"]} />
          <Field label="Titel" path={["howItWorks", "heading"]} />
          <Field label="Introtekst" path={["howItWorks", "intro"]} area />
          <List
            basePath={["howItWorks", "steps"]}
            itemLabel="Stap"
            fields={[
              { k: "title", label: "Titel" },
              { k: "desc", label: "Omschrijving", area: true },
            ]}
          />
        </Section>

        <Section value="report" sectionKey="reportPreview" title="4 · Rapport-voorbeeld" sub="Tekst naast het voorbeeldrapport (het rapport zelf is een visueel voorbeeld).">
          <Field label="Eyebrow" path={["reportPreview", "eyebrow"]} />
          <Field label="Titel" path={["reportPreview", "heading"]} />
          <Field label="Introtekst" path={["reportPreview", "intro"]} area />
          <List basePath={["reportPreview", "bullets"]} itemLabel="Opsommingspunt" />
          <Field label="Knoptekst (CTA)" path={["reportPreview", "ctaLabel"]} />
          <Field label="Disclaimer onder het voorbeeld" path={["reportPreview", "disclaimer"]} area />
        </Section>

        <Section value="audiences" sectionKey="audiences" title="5 · Voor wie" sub="Titel en de 4 doelgroep-kaarten.">
          <Field label="Eyebrow" path={["audiences", "eyebrow"]} />
          <Field label="Titel" path={["audiences", "heading"]} />
          <List
            basePath={["audiences", "items"]}
            itemLabel="Doelgroep"
            fields={[
              { k: "title", label: "Titel" },
              { k: "desc", label: "Omschrijving", area: true },
            ]}
          />
        </Section>

        <Section value="trust" sectionKey="trust" title="6 · Vertrouwen" sub="Titel en de 6 vertrouwens-kaarten.">
          <Field label="Eyebrow" path={["trust", "eyebrow"]} />
          <Field label="Titel" path={["trust", "heading"]} />
          <List
            basePath={["trust", "items"]}
            itemLabel="Kaart"
            fields={[
              { k: "title", label: "Titel" },
              { k: "desc", label: "Omschrijving", area: true },
            ]}
          />
        </Section>

        <Section value="testimonials" sectionKey="testimonials" title="7 · Testimonials" sub="Titel en de 3 klantenquotes (foto's blijven ongewijzigd).">
          <Field label="Eyebrow" path={["testimonials", "eyebrow"]} />
          <Field label="Titel" path={["testimonials", "heading"]} />
          <List
            basePath={["testimonials", "items"]}
            itemLabel="Testimonial"
            fields={[
              { k: "quote", label: "Quote", area: true },
              { k: "name", label: "Naam" },
              { k: "location", label: "Plaats" },
            ]}
          />
        </Section>

        <Section value="pricing" sectionKey="pricing" title="8 · Prijzen" sub="De twee prijskaarten, betaalmethoden en de veiligheidsregel.">
          <Field label="Eyebrow" path={["pricing", "eyebrow"]} />
          <Field label="Titel" path={["pricing", "heading"]} />

          <div className="rounded-oc border border-oc-border p-4 space-y-3 bg-oc-subtle/40">
            <p className="text-[12px] uppercase tracking-wide font-semibold text-oc-textSecondary">Enkele analyse</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Label" path={["pricing", "single", "label"]} />
              <Field label="Prijs" path={["pricing", "single", "price"]} />
            </div>
            <Field label="Subtekst" path={["pricing", "single", "sub"]} />
            <List basePath={["pricing", "single", "features"]} itemLabel="Voordeel" />
            <Field label="Knoptekst (CTA)" path={["pricing", "single", "cta"]} />
          </div>

          <div className="rounded-oc border border-oc-border p-4 space-y-3 bg-oc-subtle/40">
            <p className="text-[12px] uppercase tracking-wide font-semibold text-oc-textSecondary">3-pack bundle</p>
            <Field label="Badge" path={["pricing", "bundle", "badge"]} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Label" path={["pricing", "bundle", "label"]} />
              <Field label="Prijs" path={["pricing", "bundle", "price"]} />
            </div>
            <Field label="Subtekst" path={["pricing", "bundle", "sub"]} />
            <List basePath={["pricing", "bundle", "features"]} itemLabel="Voordeel" />
            <Field label="Knoptekst (CTA)" path={["pricing", "bundle", "cta"]} />
            <Field label="Secundaire link" path={["pricing", "bundle", "secondary"]} />
          </div>

          <Field label="Veiligheidsregel (onderaan)" path={["pricing", "securityLabel"]} />
          <List basePath={["pricing", "methods"]} itemLabel="Betaalmethode" />
        </Section>

        <Section value="faq" sectionKey="faq" title="9 · FAQ-voorbeeld" sub="Titel, 3 vragen + antwoorden en de link onderaan.">
          <Field label="Eyebrow" path={["faq", "eyebrow"]} />
          <Field label="Titel" path={["faq", "heading"]} />
          <List
            basePath={["faq", "items"]}
            itemLabel="Vraag"
            fields={[
              { k: "q", label: "Vraag" },
              { k: "a", label: "Antwoord", area: true },
            ]}
          />
          <Field label="Link onderaan" path={["faq", "linkLabel"]} />
        </Section>
      </Accordion>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white px-5">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Opslaan..." : "Opslaan"}
        </Button>
      </div>
    </div>
  );
}
